import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React from "react";

import { toast } from "@/components/ui/use-toast";
import { useI18n } from "@/context/i18n";

import {
  enrichCampaignContacts,
  getCampaignContactsByCampaignId,
  getCampaignDetailsById,
} from "./campaign-details.api";
import { IUseCampaignDetails } from "./campaign-details.hooks.interface";

export const useCampaignDetails = (campaignId?: string): IUseCampaignDetails => {
  const { t } = useI18n();
  const queryClient = useQueryClient();

  const {
    data: campaign = null,
    isLoading: isCampaignLoading,
    error: campaignError,
  } = useQuery({
    queryKey: ["campaign-details", campaignId],
    queryFn: () => getCampaignDetailsById(campaignId || ""),
    enabled: Boolean(campaignId),
  });

  const {
    data: contacts = [],
    isLoading: isContactsLoading,
    error: contactsError,
  } = useQuery({
    queryKey: ["campaign-details-contacts", campaignId],
    queryFn: () => getCampaignContactsByCampaignId(campaignId || ""),
    enabled: Boolean(campaignId),
  });

  const { mutate: enrichContacts, isPending: isEnriching } = useMutation({
    mutationFn: (variables?: { contact_ids?: string[] }) =>
      enrichCampaignContacts(campaignId || "", variables),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["campaign-details-contacts", campaignId],
      });
      toast({
        title: t("campaigns.details.enrich.success"),
        description:
          data.processed > 0
            ? `${data.processed} ${t("campaigns.details.enrich.successProcessed")}`
            : undefined,
      });
      if (data.failed > 0) {
        toast({
          title: t("campaigns.details.contacts.enrichment.failed"),
          description: `${data.failed} ${t("campaigns.details.enrich.failedCount")}`,
          variant: "destructive",
        });
      }
    },
    onError: () => {
      toast({
        title: t("generic.error"),
        description: t("campaigns.details.enrich.error"),
        variant: "destructive",
      });
    },
  });

  React.useEffect(() => {
    if (!campaignError) {
      return;
    }

    toast({
      title: t("generic.error"),
      description: t("campaigns.details.errors.loadCampaign"),
      variant: "destructive",
    });
  }, [campaignError, t]);

  React.useEffect(() => {
    if (!contactsError) {
      return;
    }

    toast({
      title: t("generic.error"),
      description: t("campaigns.details.errors.loadContacts"),
      variant: "destructive",
    });
  }, [contactsError, t]);

  return {
    campaign,
    contacts,
    isCampaignLoading,
    isContactsLoading,
    enrichContacts,
    isEnriching,
  };
};
